import { isFirefox, isSafari } from "@/lib/utils";

const getCustomAudioStream = (
  audioRef: React.MutableRefObject<HTMLAudioElement | null>,
  sourceNodeRef: React.MutableRefObject<MediaElementAudioSourceNode | null>,
  audioContextRef: React.MutableRefObject<AudioContext | null>,
): MediaStream | null => {
  if (audioRef.current && !sourceNodeRef.current) {
    const audioContext = new (window.AudioContext || window.AudioContext)();
    audioContextRef.current = audioContext;
    const sourceNode = audioContext.createMediaElementSource(audioRef.current);
    const destination = audioContext.createMediaStreamDestination();
    sourceNode.connect(destination);
    sourceNodeRef.current = sourceNode;
    sourceNode.connect(audioContext.destination);
    return destination.stream;
  }
  return null;
};

// HTMLMediaElement.captureStream() is missing in WebKit (Safari and the
// WKWebView inside ROSC Desktop on macOS, whose user agent does not say
// "Safari"), so check for the method instead of trusting the user agent.
export const supportsCaptureStream = (
  audioElement: HTMLAudioElement | null,
): boolean =>
  typeof (audioElement as unknown as { captureStream?: unknown } | null)
    ?.captureStream === "function";

export const captureElementStream = (
  audioElement: HTMLAudioElement | null,
): MediaStream | null => {
  if (!audioElement || !supportsCaptureStream(audioElement)) return null;
  try {
    return (
      audioElement as unknown as { captureStream: () => MediaStream }
    ).captureStream();
  } catch {
    return null;
  }
};

const getDefaultAudioStream = (
  audioRef: React.MutableRefObject<HTMLAudioElement | null>,
): MediaStream | null => captureElementStream(audioRef.current);

export const getAudioStream = (
  audioRef: React.MutableRefObject<HTMLAudioElement | null>,
  sourceNodeRef: React.MutableRefObject<MediaElementAudioSourceNode | null>,
  audioContextRef: React.MutableRefObject<AudioContext | null>,
): MediaStream | null => {
  if (isSafari() || isFirefox() || !supportsCaptureStream(audioRef.current)) {
    return getCustomAudioStream(audioRef, sourceNodeRef, audioContextRef);
  } else {
    return getDefaultAudioStream(audioRef);
  }
};
